/**
 * The typed edge between the SPA and the hub server.
 *
 * Every amount crosses this boundary as a decimal *string* and is turned into a
 * `bigint` here, once. `3000000000000000000` is past `Number.MAX_SAFE_INTEGER`,
 * so a JSON number would arrive already rounded — and a rounded amount that the
 * user then signs is a payment the server will refuse to verify, for a reason
 * nobody would find by reading the UI.
 */

export type UtilityName = "ASETQU_MEMBERSHIP" | "ASETQU_EVOUCHER" | "ASETQU_CATALOG";

export type Utility = {
  name: UtilityName;
  id: `0x${string}`;
  module: `0x${string}`;
  vault: `0x${string}`;
  version: number;
  status: string;
  usable: boolean;
  lockedRaw: string;
  moduleUrl?: string;
  vaultUrl?: string;
};

export type Unavailable = { code: string; detail: string };

export type MembershipPlan = {
  id: string;
  name: string;
  priceRaw: string;
  durationDays: number;
  benefits: string[];
  utility?: Utility;
  unavailable?: Unavailable;
};

export type Voucher = {
  id: string;
  name: string;
  category: string;
  benefit: string;
  priceRaw: string;
  validityDays: number;
  /** null means unlimited, which is not the same as 0. */
  stock: number | null;
};

export type Product = {
  id: string;
  slug: string;
  name: string;
  category: string;
  provider: string;
  description: string;
  priceRaw: string;
  stock: number;
  utility?: Utility;
};

export type OwnedVoucher = {
  id: string;
  code: string;
  status: string;
  purchasedAt: string;
  expiresAt: string;
  transactionHash: string;
  explorerUrl?: string;
  voucher?: Voucher;
};

export type Order = {
  id: string;
  orderNumber: string;
  quantity: number;
  amountRaw: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  transactionHash?: string;
  explorerUrl?: string;
  product?: Product;
};

export type ActivityEntry = {
  id: string;
  transactionHash: string;
  utilityName: UtilityName;
  label: string;
  amountRaw: string;
  vault: string;
  blockNumber: string;
  status: string;
  createdAt: string;
  explorerUrl?: string;
};

export type Prepared = {
  utilityId: `0x${string}`;
  utilityName: UtilityName;
  moduleAddress: `0x${string}`;
  vaultAddress: `0x${string}`;
  amountRaw: string;
  itemId?: `0x${string}`;
  itemKey?: string;
  referenceId: `0x${string}`;
  reference: string;
  expiresAt: string;
  orderNumber?: string;
};

export type VerifyResult = {
  status: "pending" | "confirmed" | "rejected";
  transactionHash: string;
  confirmations: number;
  required: number;
  utilityName?: UtilityName;
  amountRaw?: string;
  vault?: string;
  explorerUrl?: string;
  membership?: { planName: string; expiresAt: string };
  voucher?: { voucherName: string; code: string; expiresAt: string };
  order?: { orderNumber: string; productName: string; status: string };
};

export type ProtocolStats = {
  chainId: number;
  network: string;
  registry: string;
  registryUrl?: string;
  asqToken: string;
  asqTokenUrl?: string;
  totalSupplyRaw: string;
  totalLockedRaw: string;
  effectiveRaw: string;
  lockedScopeNote: string;
  utilities: Utility[];
  readAt: string;
};

export type Dashboard = {
  wallet: string;
  balanceRaw?: string;
  totalLockedRaw?: string;
  utilities?: Utility[];
  membership: { active: boolean; expiresAt?: string };
  voucherCount?: number;
  orderCount?: number;
  spentRaw?: string;
  activity?: ActivityEntry[];
};

/**
 * An error carrying the server's machine-readable code.
 *
 * The code is what the UI branches on — plan §49 wants "the registry is
 * disabled" to look different from "you are short on ASQ", and parsing a
 * sentence to tell them apart is how that breaks the first time the wording
 * changes.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    /*
      The server's problem document is `{ error, message }`, where `error` is
      the machine code and `message` is the sentence — see httpx.CodedProblem.
      The plainer `Problem` helper writes only `error`, carrying the sentence,
      so a body with no `message` means the code field is the human text.

      A proxy or a crash produces something else entirely, and that has to
      degrade to a usable error rather than to `undefined`.
    */
    let code = "REQUEST_FAILED";
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { error?: string; message?: string };
      if (body.message) {
        code = body.error ?? code;
        detail = body.message;
      } else if (body.error) {
        detail = body.error;
      }
    } catch {
      /* not JSON; the status line above is what we have */
    }
    throw new ApiError(response.status, code, detail);
  }

  return (await response.json()) as T;
}

const walletQuery = (wallet: string) => `?wallet=${encodeURIComponent(wallet)}`;

export const api = {
  utilities: () =>
    request<{ chainId: number; network: string; registry: string; utilities: Utility[] }>(
      "/api/utilities",
    ),

  membership: () => request<MembershipPlan>("/api/membership"),

  membershipStatus: (wallet: string) =>
    request<{ active: boolean; expiresAt?: string; transactionHash?: string; explorerUrl?: string }>(
      `/api/membership/status${walletQuery(wallet)}`,
    ),

  prepareMembership: (wallet: string) =>
    request<Prepared>(`/api/membership/prepare${walletQuery(wallet)}`, { method: "POST" }),

  vouchers: () =>
    request<{ vouchers: Voucher[]; categories: string[]; utility?: Utility; unavailable?: Unavailable }>(
      "/api/vouchers",
    ),

  prepareVoucher: (id: string, wallet: string) =>
    request<Prepared>(`/api/vouchers/${encodeURIComponent(id)}/prepare${walletQuery(wallet)}`, {
      method: "POST",
    }),

  myVouchers: (wallet: string) =>
    request<{ vouchers: OwnedVoucher[] }>(`/api/my-vouchers${walletQuery(wallet)}`),

  catalog: () =>
    request<{ products: Product[]; categories: string[]; utility?: Utility; unavailable?: Unavailable }>(
      "/api/catalog",
    ),

  product: (slug: string) => request<Product>(`/api/catalog/${encodeURIComponent(slug)}`),

  prepareProduct: (id: string, wallet: string) =>
    request<Prepared>(`/api/catalog/${encodeURIComponent(id)}/prepare${walletQuery(wallet)}`, {
      method: "POST",
    }),

  orders: (wallet: string) => request<{ orders: Order[] }>(`/api/orders${walletQuery(wallet)}`),

  setOrderStatus: (id: string, status: string) =>
    request<{ ok: boolean }>(`/api/orders/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  activity: (wallet: string, utility?: string) =>
    request<{ activity: ActivityEntry[] }>(
      `/api/activity${walletQuery(wallet)}${utility ? `&utility=${encodeURIComponent(utility)}` : ""}`,
    ),

  dashboard: (wallet: string) => request<Dashboard>(`/api/dashboard${walletQuery(wallet)}`),

  verify: (transactionHash: string) =>
    request<VerifyResult>("/api/transactions/verify", {
      method: "POST",
      body: JSON.stringify({ transactionHash }),
    }),

  protocol: () => request<ProtocolStats>("/api/protocol/stats"),

  devState: (wallet?: string) =>
    request<Record<string, unknown>>(`/api/dev/state${wallet ? walletQuery(wallet) : ""}`),

  devReset: () => request<{ ok: boolean; note: string }>("/api/dev/reset", { method: "POST" }),
};
